import React from "react";
import { alpha } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import { useTable, useBlockLayout, useFlexLayout } from "react-table";
import styled from "@emotion/styled";
import styleVariables from "../../helpers/styleVariables";
import { Property } from "csstype";
import TextAlign = Property.TextAlign;
import getLeaderboards from "../../api/queries/getLeaderboards";
import InfiniteLoader from "react-window-infinite-loader";
import { FixedSizeList as List } from "react-window";
import Link from "../../components/typo/link/link";
import { FilterGetParams } from "../../../pages/leaderboards";
import { FilterOperator } from "../../api/types";
import { renameFilterColumns } from "../podium/podium";
import { useTranslation } from "next-i18next";
import Flag, { FlagSize } from "../flag/flag";
import useChangeFilterInstantly from "../../hooks/useChangeFilterInstantly";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import useMediaQuery from "@mui/material/useMediaQuery";
import breakpoints from "../../helpers/breakpoints";
import { useRouter } from "next/router";
import Image from "next/image";

const Root = styled.div`
  .table {
    margin-bottom: 0;
    margin-left: -16px;
    margin-right: -16px;
    width: calc(100% + 32px);
    @media (min-width: ${breakpoints.min.md}) {
      margin-left: 0;
      margin-right: 0;
      width: 100%;
      .headingTr {
        overflow-y: scroll;
        overflow-x: hidden;
        position: relative;
      }
    }
  }
`;

const Row = styled.div<{ even: boolean; active: boolean }>`
  align-items: center;
  color: ${(props) => (props.active ? "white" : "unset")};
  background: ${(props) =>
    props.active
      ? styleVariables.colors.blueRegular
      : styleVariables.colors.white};
  *, a {
    color: ${(props) => (props.active ? "white!important" : "")};
  }
  @media (min-width: ${breakpoints.min.md}) {
    background: ${(props) =>
      props.active
        ? styleVariables.colors.blueRegular
        : props.even
        ? styleVariables.colors.blueLightest
        : styleVariables.colors.white};
  }
`;

const TableHeading = styled.div<{ textAlign?: TextAlign }>`
  text-align: ${(props) => (props.textAlign ? props.textAlign : "unset")};
  font-size: 14px;
  color: ${styleVariables.colors.grey};
  background: ${styleVariables.colors.white};
  padding: 9px 12px;
`;

const TableHeadingShooterName = styled.div`
  text-align: left;
  font-size: 14px;
  color: ${styleVariables.colors.grey};
  background: ${styleVariables.colors.white};
  padding: 9px 12px 9px 4px;
  @media (min-width: ${breakpoints.min.md}) {
    padding: 9px 12px;
  }
`;

const AvatarCell = styled.div`
  padding: 0 8px;
  display: flex;
  justifycontent: center;
`;

const ScoreCell = styled.div`
  font-size: 18px;
  text-align: right;
  padding: 0 12px 0 8px;
  @media (min-width: 600px) {
    padding: 0 12px;
    font-size: 32px;
  }
`;

const RankCell = styled.div`
  font-size: 12px;
  text-align: center;
  padding: 0 4px 0 16px;
  @media (min-width: ${breakpoints.min.md}) {
    padding: 0 8px;
    font-size: 24px;
  }
`;

const DisplayNameCell = styled.div`
  font-size: 16px;
  text-align: left;
  padding: 0 4px;
  @media (min-width: ${breakpoints.min.md}) {
    padding: 0 12px;
    font-size: 32px;
  }
`;

const MobileInfoBelowName = styled.div`
  display: flex;
  font-size: 12px;
  color: ${styleVariables.colors.grey};

  > * {
    margin-right: 4px;
  }
`;

const TableBody = styled.div`
  border-color: ${alpha(styleVariables.colors.grey, 0.25)};
  border-width: 2px 0;
  border-style: solid;
  position: relative;

  &:before {
    left: 0px;
    right: 0;
    top: 0;
    background: linear-gradient(
      180deg,
      rgba(83, 87, 96, 0.1) 0%,
      rgba(83, 87, 96, 0) 100%
    );
  }

  &:after {
    left: 0px;
    right: 0;
    bottom: 0;
    background: linear-gradient(
      0deg,
      rgba(83, 87, 96, 0.1) 0%,
      rgba(83, 87, 96, 0) 100%
    );
  }

  &:after,
  &:before {
    content: "";
    display: block;
    position: absolute;
    height: 30px;
    z-index: 1;
    background-blend-mode: multiply;
    pointer-events: none;
  }

  .hideScrollbarInHeading {
    display: none;
    @media (min-width: ${breakpoints.min.md}) {
      display: block;
      position: absolute;
      top: -42px;
      right: 0;
      background: white;
      width: 23px;
      height: 40px;
      z-index: 55;
    }
  }
`;

interface TableLeaderboardProps {
  shooterIdToHighlight?: number;
  query: FilterGetParams;
}

const TableLeaderboard: React.FC<TableLeaderboardProps> = ({
  shooterIdToHighlight,
  query,
}) => {
  const router = useRouter();
  const isMdUp = useMediaQuery(`(min-width: ${breakpoints.min.md})`);

  const ITEM_SIZE = isMdUp ? 92 : 63;
  const PAGE_SIZE = 20;
  const LIST_ROWS = isMdUp ? 7 : 5;
  const LIST_HEIGHT = LIST_ROWS * ITEM_SIZE;

  const { handleSubmitFilterInstantChange } = useChangeFilterInstantly(query);
  const { t } = useTranslation();
  const [tableData, setTableData] = React.useState<any[]>([]);
  const [itemCount, setItemCount] = React.useState<number | undefined>(
    undefined
  );
  const [rangeToCache, setRangeToCache] = React.useState<number[]>([]);
  const [tableScrolled, setTableScrolled] = React.useState<boolean>(false);
  const [tableLoadedWithShooterId, setTableLoadedWithShooterId] =
    React.useState<boolean>(false);

  const filter = Object.entries(query)
    .filter((item) => item[0] !== "id")
    .map((item) => ({
      column: renameFilterColumns(item[0]),
      value: item[1],
      operator: "eq" as FilterOperator,
    }));

  const loadData = async (to: number) => {
    if (rangeToCache.includes(to)) {
      return false;
    }
    const response = await getLeaderboards({
      shooterId:
        tableLoadedWithShooterId === false && shooterIdToHighlight
          ? shooterIdToHighlight
          : undefined,
      range: { from: to - PAGE_SIZE + 1, to: to },
      filter,
    });
    if (response && itemCount === undefined) {
      setItemCount(response.metadata.count);
      if (shooterIdToHighlight) {
        setTableLoadedWithShooterId(true);
      }
    }
    if (response) {
      const tableDataCurrent =
        tableData.length > 0
          ? tableData
          : new Array(response.metadata.count).fill({ user: undefined });
      response.data.forEach((item, key) => {
        // @ts-ignore
        tableDataCurrent[key + response.metadata.page.from] = item;
      });
      setTableData([...tableDataCurrent]);
    }
  };

  React.useEffect(() => {
    setTableData([]);
    setItemCount(undefined);
    setRangeToCache([]);
    setTableScrolled(false);
    setTableLoadedWithShooterId(false);
  }, [query]);

  React.useEffect(() => {
    // this is the first call
    if (
      tableData.length === 0 &&
      itemCount === undefined &&
      rangeToCache.length === 0 &&
      tableScrolled === false
    ) {
      loadData(PAGE_SIZE - 1);
      if (shooterIdToHighlight === undefined) {
        setRangeToCache([PAGE_SIZE - 1]);
      }
    }
  }, [tableData, itemCount, rangeToCache, tableScrolled]);

  React.useEffect(() => {
    if (tableScrolled === false && tableData.length > 0) {
      const scrollUserIndex = shooterIdToHighlight
        ? tableData.findIndex(({ user }) => user?.id === shooterIdToHighlight)
        : 3;
      const scrollToY = shooterIdToHighlight
        ? ITEM_SIZE * scrollUserIndex - Math.floor(LIST_ROWS / 2) * ITEM_SIZE
        : ITEM_SIZE * scrollUserIndex;
      document.querySelector("#tableBody > div")?.scrollTo(0, scrollToY);
      setTimeout(() => {
        setTableScrolled(true);
      }, 1000);
    }
  }, [tableData, tableScrolled]);

  const columns = React.useMemo(
    () => [
      {
        Header: () => <TableHeading>{t("tableLeaderboard.rank")}</TableHeading>,
        accessor: "rank",
        Cell: (props: any) => {
          if (props.value) {
            return <RankCell>{props.value} </RankCell>;
          } else {
            return (
              <Box display="flex" justifyContent="center">
                <Skeleton
                  variant="rectangular"
                  width={isMdUp ? 40 : 20}
                  height={isMdUp ? 30 : 15}
                />
              </Box>
            );
          }
        },
        width: isMdUp ? 50 : 20,
      },
      {
        Header: "",
        accessor: "avatar",
        Cell: (props: any) => {
          const size = isMdUp ? 60 : 34;
          if (props.row.original.user?.id) {
            return (
              <AvatarCell>
                {props.row.original.user.avatar === null ? (
                  <Avatar sx={{ width: size, height: size }} src={undefined} />
                ) : (
                  <Image
                    src={props.row.original.user.avatar}
                    className="img-fluid rounded-circle"
                    alt=""
                    width={size}
                    height={size}
                    objectFit="cover"
                  />
                )}
              </AvatarCell>
            );
          } else {
            return <Skeleton variant="circular" width={size} height={size} />;
          }
        },
        width: isMdUp ? 60 : 25,
      },
      {
        Header: () => (
          <TableHeadingShooterName>
            {t("tableLeaderboard.shooterName")}
          </TableHeadingShooterName>
        ),
        accessor: "user.displayName",
        width: isMdUp ? 300 : 100,
        Cell: (props: any) => {
          return (
            <DisplayNameCell>
              {props.row.original.user?.id ? (
                <>
                  <Link
                    href={`/shooter/${props.row.original.user.id}?discipline=${query.discipline}`}
                  >
                    {props.value}
                  </Link>
                  {!isMdUp && (
                    <MobileInfoBelowName>
                      <Flag
                        size={FlagSize.small}
                        code={props.row.original.country.code}
                        disabled={true}
                      />
                      {props.row.original.club?.name ?? '-'}
                    </MobileInfoBelowName>
                  )}
                </>
              ) : (
                <div>
                  <Skeleton variant="rectangular" width={80} height={20} />
                </div>
              )}
            </DisplayNameCell>
          );
        },
      },
      {
        Header: () => (
          <Box pl={4}>
            <TableHeading>{t("tableLeaderboard.country")}</TableHeading>
          </Box>
        ),
        id: "country",
        accessor: "country.code",
        Cell: (props: any) => {
          if (props.row.original.country?.code) {
            return (
              <Flag
                size={FlagSize.large}
                code={props.row.original.country.code}
                disabled={Boolean(query.id)}
                text={props.row.original.country.code3}
                onClick={() =>
                  handleSubmitFilterInstantChange(
                    "country",
                    props.row.original.country.id
                  )
                }
              />
            );
          } else {
            return (
              <Box display="flex" alignItems="center">
                <Box mr={1}>
                  <Skeleton variant="circular" width={32} height={32} />
                </Box>
                <Skeleton variant="rectangular" width={40} height={20} />
              </Box>
            );
          }
        },
        width: 100,
      },
      {
        Header: () => (
          <TableHeading textAlign="center">
            {t("tableLeaderboard.club")}
          </TableHeading>
        ),
        id: "club",
        accessor: "club.name",
        width: 100,
        Cell: (props: any) => {
          if (props.row.original.club?.name) {
            return (
              <Box display="flex" justifyContent="center">
                <Link
                  onClick={() =>
                    handleSubmitFilterInstantChange(
                      "club",
                      props.row.original.club.id
                    )
                  }
                  disabled={Boolean(query.id)}
                  isBold={true}
                >
                  {props.row.original.club.name}
                </Link>
              </Box>
            );
          } else if (props.row.original.user?.id) {
            return (
              <Box display="flex" justifyContent="center">
                -
              </Box>
            );
          } else {
            return (
              <Box display="flex" justifyContent="center">
                <Skeleton variant="rectangular" width={80} height={20} />
              </Box>
            );
          }
        },
      },
      {
        Header: () => (
          <TableHeading textAlign="right">
            {t("tableLeaderboard.score")}
          </TableHeading>
        ),
        width: isMdUp ? 100 : 50,
        id: "score",
        Cell: (props: any) => {
          if (props.row.original.avgScore) {
            return (
              <ScoreCell>
                {query.display === "averageScore"
                  ? props.row.original.avgScore.toFixed(3)
                  : props.row.original.totalScore.toFixed(props.row.original.discipline === '10m_air_pistol' ? 0 : 1)}
              </ScoreCell>
            );
          } else {
            return (
              <Box display="flex" justifyContent="flex-end" mr={1}>
                <Skeleton
                  variant="rectangular"
                  width={isMdUp ? 80 : 40}
                  height={isMdUp ? 15 : 15}
                />
              </Box>
            );
          }
        },
      },
    ],
    [query.display, isMdUp, router]
  );

  const defaultColumn = React.useMemo(
    () => ({
      width: 150,
    }),
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    totalColumnsWidth,
    prepareRow,
    setHiddenColumns,
  } = useTable(
    {
      columns,
      data: tableData,
      defaultColumn,
    },
    useFlexLayout
  );

  const RenderRow = React.useCallback(
    ({ index, style }) => {
      const row = rows[index];
      if (row === undefined) {
        return (
          <Row even={false} active={false} className="tr" style={style}>
            <div>loading...</div>
          </Row>
        );
      } else {
        prepareRow(row);
        const active =
          shooterIdToHighlight &&
          (row.original as any).user?.id === shooterIdToHighlight;
        return (
          <Row
            active={Boolean(active)}
            {...row.getRowProps({
              style,
            })}
            even={row.index % 2 === 0}
            className="tr"
          >
            {row.cells.map((cell) => {
              return (
                <div {...cell.getCellProps()} className="td">
                  {cell.render("Cell")}
                </div>
              );
            })}
          </Row>
        );
      }
    },
    [prepareRow, rows]
  );

  const loadDataOnScroll = (to: number) => {
    if (
      (shooterIdToHighlight && tableLoadedWithShooterId) ||
      !shooterIdToHighlight
    ) {
      setRangeToCache((prev) => [...prev, to]);
      loadData(to);
    }
  };

  const loadMoreItems = async (startIndex: number, stopIndex: number) => {
    const from = Math.ceil(startIndex / PAGE_SIZE) * PAGE_SIZE - 1;
    const to = Math.ceil(stopIndex / PAGE_SIZE) * PAGE_SIZE - 1;
    if (!rangeToCache.includes(from) && from >= PAGE_SIZE - 1) {
      // this ensures loading data when user scrolls up
      loadDataOnScroll(from);
    }
    if (!rangeToCache.includes(to)) {
      // this ensures loading data when user scrolls down
      loadDataOnScroll(to);
    }
  };

  React.useEffect(() => {
    if (!isMdUp) {
      setHiddenColumns(["club", "country"]);
    }
  }, [isMdUp, tableData]);

  if (tableData.length === 0 || itemCount === undefined) {
    return null;
  }

  return (
    <Root>
      <div {...getTableProps()} className="table">
        <div>
          {headerGroups.map((headerGroup) => (
            <div
              {...headerGroup.getHeaderGroupProps()}
              className="tr headingTr"
            >
              {headerGroup.headers.map((column) => (
                <div {...column.getHeaderProps()} className="th">
                  {column.render("Header")}
                </div>
              ))}
            </div>
          ))}
        </div>

        <TableBody {...getTableBodyProps()} id="tableBody">
          <span className="hideScrollbarInHeading"></span>
          <InfiniteLoader
            isItemLoaded={() => false}
            itemCount={itemCount}
            loadMoreItems={loadMoreItems}
            minimumBatchSize={PAGE_SIZE}
            threshold={1}
          >
            {({ onItemsRendered, ref }: any) => (
              <List
                itemCount={itemCount}
                onItemsRendered={onItemsRendered}
                ref={ref}
                height={LIST_HEIGHT}
                itemSize={ITEM_SIZE}
                width={"100%"}
              >
                {RenderRow}
              </List>
            )}
          </InfiniteLoader>
        </TableBody>
      </div>
    </Root>
  );
};

export default TableLeaderboard;
